import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const capitalizeWords = (value: string) => {
  return value
    .toLocaleLowerCase('pt-BR')
    .replace(/(^|[\s\-’'"])(\p{L})/gu, (_, sep, letter) => sep + letter.toLocaleUpperCase('pt-BR'));
};

// Cache de Intl.DateTimeFormat por combinação de opções. Instanciar um
// DateTimeFormat é caro (carrega dados de locale); em tabelas com dezenas de
// células de data por render isso dominava o custo. A chave é o JSON das opções
// já mescladas — o conjunto de combinações é pequeno e finito.
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();
const getDateTimeFormat = (
  opts: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  const key = JSON.stringify(opts);
  let fmt = dateTimeFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('pt-BR', opts);
    dateTimeFormatCache.set(key, fmt);
  }
  return fmt;
};

/**
 * Converte string/Date pra um Date nativo, tratando string YYYY-MM-DD (sem
 * hora) como data local em vez de deixar o `new Date(str)` padrão interpretar
 * como UTC — evita a data exibida "voltar um dia" dependendo do fuso do
 * usuário. Usado internamente por `formatDate`. Para um Date que vá virar
 * célula de Excel, use `toExcelDate` (semântica de fuso diferente).
 */
export function parseDateValue(date: string | Date): Date | null {
  if (!date) return null;

  let dateObj: Date;
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Data sem hora: criar como data local para evitar problemas de fuso horário
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  return isNaN(dateObj.getTime()) ? null : dateObj;
}

/**
 * Converte pra um `Date` nativo apto a virar valor de célula do Excel
 * (`exceljs`). ExcelJS serializa `date.getTime()` cru — sem NENHUMA
 * conversão de fuso (ver `dateToExcel` em `exceljs/lib/utils/utils.js`) — e
 * quem abre o arquivo lê esse serial como se já fosse o wall clock a
 * exibir. Datas puras (`YYYY-MM-DD`) não têm fuso: viram meia-noite UTC do
 * mesmo dia. Timestamps completos são reinterpretados no fuso de exibição
 * do app (America/Sao_Paulo — igual ao `formatDate`), não no fuso do
 * navegador de quem exporta, senão a célula sai com o dia/hora errados pra
 * quem estiver fora do fuso de Brasília.
 */
/** numFmt aplicado à célula que recebe o `Date` de `toExcelDate` — os três
 * exports client-side sempre mostram só a data (sem hora, ver `toExcelDate`),
 * então é um único formato, não um mapa por coluna como no backend. */
export const EXCEL_DATE_NUMFMT = 'dd/mm/yyyy';

export function toExcelDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null;

  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }

  const instant = date instanceof Date ? date : new Date(date);
  if (isNaN(instant.getTime())) return null;

  // Reaproveita o cache de Intl.DateTimeFormat já existente (getDateTimeFormat)
  // — instanciar um a cada chamada dominaria o tempo de exports grandes.
  const parts = getDateTimeFormat({
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);

  return new Date(
    Date.UTC(
      part('year'),
      part('month') - 1,
      part('day'),
      part('hour') % 24, // Intl pode devolver "24" à meia-noite em alguns runtimes
      part('minute'),
      part('second'),
    ),
  );
}

export const formatDate = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string => {
  const dateObj = parseDateValue(date);
  if (!dateObj) return 'N/A';

  const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    // Não usar timeZone para datas apenas (sem hora) para evitar conversão de fuso horário
    ...(isDateOnly ? {} : { timeZone: 'America/Sao_Paulo' })
  };

  return getDateTimeFormat({ ...defaultOptions, ...options }).format(dateObj);
};


export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo'
  })
}

/** Data e hora (como `formatDateTime`); `—` quando vazio. */
export const formatDateTimeOrDash = (date: string | null | undefined): string =>
  date ? formatDateTime(date) : '—'

// Função para formatar status do processo
export const formatStatus = (status: string): string => {
  if (!status) return status;

  // Mapeamento específico para status de comparação e outros status especiais
  const statusMap: Record<string, string> = {
    'completed': 'Completa',
    'in_progress': 'Em Andamento',
    'pending': 'Pendente',
    'processing': 'Processando',
    'failed': 'Falhou',
    'cancelled': 'Cancelado',
    'desvio_finalizado': 'Desvio Finalizado',
    'desvio finalizado': 'Desvio Finalizado',
  };

  // Se existe mapeamento específico, retornar
  const lowerStatus = status.toLowerCase().trim();
  if (statusMap[lowerStatus]) {
    return statusMap[lowerStatus];
  }

  // Formatação padrão: capitalizar palavras e substituir underscores por espaços
  // Primeiro, substituir underscores por espaços
  let formatted = status.replace(/_/g, ' ');

  // Normalizar espaços múltiplos e trim
  formatted = formatted.replace(/\s+/g, ' ').trim();

  // Tratar padrão "Ag" sem ponto seguido de siglas ou palavras
  // Ex: "Ag Ce Ou Cct" -> "Ag. CE ou CCT"
  formatted = formatted.replace(/\b(ag|Ag|AG)\s+([A-Z]{2,})\s+(ou|Ou|OU)\s+([A-Z]{2,})\b/gi, (_match, _prefix, sigla1, _conjuncao, sigla2) => {
    return 'Ag. ' + sigla1.toUpperCase() + ' ou ' + sigla2.toUpperCase();
  });

  // Tratar padrão "Ag" sem ponto seguido de uma única sigla
  // Ex: "Ag Ce" -> "Ag. CE"
  formatted = formatted.replace(/\b(ag|Ag|AG)\s+([A-Z]{2,})\b/gi, (_match, _prefix, sigla) => {
    return 'Ag. ' + sigla.toUpperCase();
  });

  // Tratar padrão "Ag." primeiro - separar se estiver junto com a próxima palavra
  // Ex: "Ag.finalização" -> "Ag. finalização", "ag.finalização" -> "Ag. finalização"
  formatted = formatted.replace(/\b(ag|Ag|AG)\.([a-záàâãéêíóôõúçA-Z])/gi, (_match, _prefix, next) => {
    return 'Ag. ' + next;
  });

  // Garantir que "Ag." sempre tenha espaço após (normalizar)
  formatted = formatted.replace(/\b(ag|Ag|AG)\.\s*/gi, 'Ag. ');

  // Processar cada palavra
  const words = formatted.split(' ').filter(w => w.length > 0);
  const formattedWords = words.map((word) => {
    if (!word) return word;

    // Se é "Ag." (já normalizado), manter como está
    if (word === 'Ag.') {
      return word;
    }

    // Se é "ou" (conjunção), manter em minúsculas
    if (word.toLowerCase() === 'ou') {
      return 'ou';
    }

    // Se a palavra contém "/" (ex: DI/DUIMP), manter em maiúsculas
    if (word.includes('/')) {
      return word.toUpperCase();
    }

    // Se é uma sigla curta (2-4 letras maiúsculas), manter em maiúsculas
    if (word.length >= 2 && word.length <= 4 && /^[A-Z]+$/.test(word)) {
      return word;
    }

    // Capitalizar primeira letra e manter o resto em minúsculo
    // Isso garante que todas as palavras sejam capitalizadas corretamente
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  formatted = formattedWords.join(' ');

  // Ajustes finais
  // Garantir que "Ag." sempre tenha espaço após
  formatted = formatted.replace(/Ag\.([^\s])/g, 'Ag. $1');

  // Normalizar espaços novamente após ajustes
  formatted = formatted.replace(/\s+/g, ' ').trim();

  return formatted;
};

// Função específica para data de criação
export const formatCreatedDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return formatDate(dateString, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formata o nome completo do usuário (igual ao usado no perfil/header)
 * Usa first_name + last_name, ou name, ou email como fallback
 */
export const getUserFullName = (user?: {
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
}): string => {
  if (!user) return 'Usuário Desconhecido';

  // Prioridade: first_name + last_name > name > email
  if (user.first_name || user.last_name) {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    if (fullName) return fullName;
  }

  if (user.name) return user.name;
  if (user.email) return user.email;

  return 'Usuário Desconhecido';
};

/**
 * Formata um timestamp do banco convertendo explicitamente para America/Sao_Paulo.
 *
 * As colunas de origem são `timestamp without time zone` gravadas em horário de
 * Brasília (BRT-bare); o driver pg as hidrata como Date interpretando-as nesse
 * mesmo fuso, então o instante UTC resultante é correto — só falta pedir a
 * exibição de volta em America/Sao_Paulo (em vez de deixar o navegador decidir).
 */
export function formatRawDbDateTime(value?: string | Date | null): string {
  if (value === null || value === undefined) return '';
  return formatDate(value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatRawDbDate(value?: string | Date | null): string {
  if (value === null || value === undefined) return '';
  return formatDate(value);
}

export function formatCurrencyUSD(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatWeightKg(value: number): string {
  return (
    new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value) + ' kg'
  );
}

type TzMode = 'to-datetime-local' | 'from-datetime-local' | 'to-date' | 'from-date';

/**
 * tzDate: converte datas sem te ferrar com -3h
 *
 * Regras:
 * - datetime-local na UI: "YYYY-MM-DDTHH:mm" (LOCAL)
 * - API: ISO UTC "...Z"
 */
export function tzDate(value: any, mode: TzMode): string | null {
  if (value === undefined || value === null) return mode.startsWith('to-') ? '' : null;

  // ---------- DATE ONLY ----------
  if (mode === 'to-date') {
    // se já é YYYY-MM-DD, mantém
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  if (mode === 'from-date') {
    const s = String(value ?? '').trim();
    return s ? s : null; // backend recebe "YYYY-MM-DD" ou null
  }

  // ---------- DATETIME-LOCAL ----------
  if (mode === 'to-datetime-local') {
    // se já é datetime-local, NÃO mexe (evita -3h)
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;

    const d = value instanceof Date ? value : new Date(value); // ISO com Z cai aqui
    if (isNaN(d.getTime())) return '';

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}`;
  }

  if (mode === 'from-datetime-local') {
    const s = String(value ?? '').trim();
    if (!s) return null;

    // espera "YYYY-MM-DDTHH:mm"
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) return null;

    const [, yy, mo, dd, hh, mm] = m;
    // cria DATE LOCAL e manda como UTC ISO (Z)
    const local = new Date(
      Number(yy),
      Number(mo) - 1,
      Number(dd),
      Number(hh),
      Number(mm),
      0,
      0
    );
    return isNaN(local.getTime()) ? null : local.toISOString();
  }

  return null;
}